import { AudioManager } from "../../AudioManager";
import { GameVars } from "../../GameVars";
import { Block } from "./Block";
import { BoardContainer } from "./BoardContainer";
import { BubblesContainer } from "./BubblesContainer";
import { Cell } from "./Cell";
import { SpecialCellHoverEffect } from "./SpecialCellHoverEffect";
import { BoardManager } from "./BoardManager";
import * as Templok from "@ravalmatic/templok-engine";

export class GridContainer extends Phaser.GameObjects.Container {

    public static readonly CELL_SIZE = 78.5;

    public markingPossibleLinesAndColumns: boolean;
    public blocksContainer: Phaser.GameObjects.Container;

    private cells: Cell[];
    private markers: Phaser.GameObjects.Image[];
    private shadows: Phaser.GameObjects.Image[];
    private markersContainer: Phaser.GameObjects.Container;
    private shadowsContainer: Phaser.GameObjects.Container;
    private f: number;
    private possibleLines: number[];
    private possibleColumns: number[];
    private bubblesContainer: BubblesContainer;
    private specialCellHoverEffect: SpecialCellHoverEffect;
    private premarkedFeatureEffectPlayed: boolean;
    
    constructor(scene: Phaser.Scene) {
        
        super(scene);

        this.cells = [];
        this.markers = [];
        this.f = 0;
        this.markingPossibleLinesAndColumns = false;
        this.specialCellHoverEffect = null;
        this.premarkedFeatureEffectPlayed = false;

        this.shadowsContainer = new Phaser.GameObjects.Container(this.scene);
        this.add(this.shadowsContainer);

        this.shadows = [];

        for (let i = 0; i < 9; i ++) {
            const shadow = new Phaser.GameObjects.Image(this.scene, 0, 0, "texture_atlas_1", "gem_00");
            shadow.alpha = .35;
            shadow.visible = false;
            this.shadowsContainer.add(shadow);

            this.shadows.push(shadow);
        }

        this.markersContainer = new Phaser.GameObjects.Container(this.scene);
        this.add(this.markersContainer);

        this.blocksContainer = new Phaser.GameObjects.Container(this.scene);
        this.add(this.blocksContainer);

        this.bubblesContainer = new BubblesContainer(this.scene);
        this.add(this.bubblesContainer);
    }

    public update(): void {

        if (this.markingPossibleLinesAndColumns) {

            this.f = Math.round(this.f) > Templok.Engine.GRID_SIZE - 1 ? 0 : this.f + .55;

            for (let i = 0; i < this.possibleLines.length; i++) {

                const r = this.possibleLines[i];
    
                for (let c = 0; c < Templok.Engine.GRID_SIZE; c ++) {
    
                    const cell = this.getCell({c: c, r: r});
    
                    if (cell) {

                        let s = cell.scaleX;
                        if (c === Math.round(this.f)) {
                            s *= 1.065;
                            cell.setScale(s);
                        } else {
                            s *= .99;
                            if (s < 1) {
                                s = 1;
                            }
                            cell.setScale(s);
                        }
                    }
                }
            }
    
            for (let i = 0; i < this.possibleColumns.length; i++) {
    
                const c = this.possibleColumns[i];
    
                for (let r = 0; r < Templok.Engine.GRID_SIZE; r ++) {
    
                    const cell = this.getCell({c: c, r: r});
    
                    if (cell) {

                        let s = cell.scaleX;
                        if (r === Math.round(this.f)) {
                            s *= 1.065;
                            cell.setScale(s);
                        } else {
                            s *= .99;
                            if (s < 1) {
                                s = 1;
                            }
                            cell.setScale(s);
                        }
                    }
                }
            }

        } else {

            this.cells.forEach(function(cell: Cell): void {
                let s = cell.scaleX;
                s *= .99;
                if (s < 1) {
                    s = 1;
                }
                cell.setScale(s);
            });
        }

        this.cells.forEach(function(cell: Cell): void {
            cell.update();
        });

        this.bubblesContainer.update();

        if (GameVars.numOfCellDestroyed > 0) {
            Cell.f ++;
        }
    }

    public onGameStarted(): void {

        const board = GameVars.templokEngine.board;

        for (let c = 0; c < Templok.Engine.GRID_SIZE; c ++) {

            for (let r = 0; r < Templok.Engine.GRID_SIZE; r ++) {

                if (board[c][r] >= Templok.CellType.TAKEN || board[c][r] === Templok.CellType.UNBREAKABLE) {
                    
                    const x = GridContainer.CELL_SIZE * (c - (Templok.Engine.GRID_SIZE / 2 - .5));
                    const y = GridContainer.CELL_SIZE * (r - (Templok.Engine.GRID_SIZE / 2 - .5));

                    const cellColor = board[c][r] >= Templok.CellType.TAKEN ? 0 : -1;

                    const cell = new Cell(this.scene, {c: c, r: r}, cellColor, this, board[c][r]);
                    cell.x = x;
                    cell.y = y;
                    this.blocksContainer.add(cell);

                    this.cells.push(cell);
                } 
            }
        }
    }

    public removeCell(cell: Cell): void {

        const i = this.cells.indexOf(cell);

        if (i !== -1) {

            this.cells.splice(i, 1);
            cell.destroy();
        }
    }

    public addBlock(block: Block, p: {c: number, r: number}): void {

        const blockData = block.blockData;
        const data = blockData.data[blockData.i];
        const color = block.color;

        // EL CASO DEL BLOQUE ALARGADO DE 5 CELDAS CON SUS 2 VARIANTES
        if (data === 0x0001 || data === 0x0002) {

            for (let i = 0; i < 5; i ++) {

                const pCell = {c: p.c, r: p.r};

                if (data === 0x0001) {
                    pCell.c += i;
                } else {
                    pCell.r += i;
                }

                const cell = new Cell(this.scene, pCell, color, this);
                cell.x = GridContainer.CELL_SIZE * (pCell.c - (Templok.Engine.GRID_SIZE / 2 - .5));
                cell.y = GridContainer.CELL_SIZE * (pCell.r - (Templok.Engine.GRID_SIZE / 2 - .5));
                this.blocksContainer.add(cell);

                this.cells.push(cell);

                this.bubblesContainer.emitBubbles(cell);
            }

        } else {

            for (let c = 0; c < 4; c++) {
                for (let r = 0; r < 4; r++) {
                    if (data & (0x8000 >> (c * 4 + r))) {

                        const pCell = {c: p.c, r: p.r};
    
                        pCell.c += c;
                        pCell.r += r;
                        
                        const cell = new Cell(this.scene, pCell, color, this);
                        cell.x = GridContainer.CELL_SIZE * (pCell.c - (Templok.Engine.GRID_SIZE / 2 - .5));
                        cell.y = GridContainer.CELL_SIZE * (pCell.r - (Templok.Engine.GRID_SIZE / 2 - .5));
                        this.blocksContainer.add(cell);
    
                        this.cells.push(cell);

                        this.bubblesContainer.emitBubbles(cell);
                    }
                }
            }
        }

        if (blockData.n < 3) {
            AudioManager.playSound("piece_small_released");
        } else if (blockData.n < 5) {
            AudioManager.playSound("piece_medium_released");
        } else {
            AudioManager.playSound("piece_large_released");
        }
    }

    public onMatch(lines: number[], columns: number[], duplicatedCellsData: {p: {c: number, r: number}, type: number}[]): void {

        this.unMarkPossibleLinesAndColumns();

        for (let i = 0; i < duplicatedCellsData.length; i++) {
            const data = duplicatedCellsData[i];
            const cell = this.getCell(data.p);
            cell.specialCell = true;
            cell.specialCellType = data.type;
        }

        let n = 0;
        let isCombo = lines.length + columns.length >= 3;

        for (let i = 0; i < lines.length; i++) {

            const r = lines[i];

            for (let c = 0; c < Templok.Engine.GRID_SIZE; c ++) {

                const cell = this.getCell({c: c, r: r});

                if (cell) {

                    cell.onMatch(duplicatedCellsData.length > 0 || isCombo ? n : 0, isCombo, lines.length + columns.length);

                    if (cell.specialCell) {

                        BoardContainer.currentInstance.addSpecialCell({c: c, r: r}, cell.specialCellType, c, duplicatedCellsData.length > 0 || isCombo ? n : 0);

                    } else {

                        this.blocksContainer.bringToTop(cell);
                        this.bubblesContainer.emitBubbles(cell);
                    }
                }
            }

            n ++;
        }

        for (let i = 0; i < columns.length; i++) {

            const c = columns[i];

            for (let r = 0; r < Templok.Engine.GRID_SIZE; r ++) {

                const cell = this.getCell({c: c, r: r});

                if (cell) {

                    cell.onMatch(duplicatedCellsData.length > 0 || isCombo ? n : 0, isCombo, lines.length + columns.length);

                    if (!cell.specialCell) {
                        
                        this.blocksContainer.bringToTop(cell);
                        this.bubblesContainer.emitBubbles(cell);
                    }
                }
            }

            n ++;
        }

        if (duplicatedCellsData.length > 0 || isCombo ) {

            for (let i = 0; i < n; i ++) {
                
                let delay = isCombo ? 750 * i + 1000 : 750 * i;
                this.scene.time.delayedCall(delay, function(): void {
                    AudioManager.playSound("cleared_line");
                }, [], this);
            }

        } else {
            AudioManager.playSound("cleared_line");
        }

        this.checkCellsForInconsistencies();
    }

    public markDropPosition(p: {c: number, r: number}, blockData: Templok.BlockData): void {

        const data = blockData.data[blockData.i];
        
        // EL CASO DEL BLOQUE ALARGADO DE 5 CELDAS CON SUS 2 VARIANTES
        if (data === 0x0001 || data === 0x0002) {

            for (let i = 0; i < 5; i ++) {

                const pCell = {c: p.c, r: p.r};

                if (data === 0x0001) {
                    pCell.c += i;
                } else {
                    pCell.r += i;
                }

                const x = GridContainer.CELL_SIZE * (pCell.c - (Templok.Engine.GRID_SIZE / 2 - .5));
                const y = GridContainer.CELL_SIZE * (pCell.r - (Templok.Engine.GRID_SIZE / 2 - .5));

                const marker = new Phaser.GameObjects.Image(this.scene, x, y, "texture_atlas_3", "highlighted_cell");
                this.markersContainer.add(marker);

                this.markers.push(marker);
            }

        } else {

            for (let c = 0; c < 4; c++) {
                for (let r = 0; r < 4; r++) {
                    if (data & (0x8000 >> (c * 4 + r))) {

                        const pCell = {c: p.c, r: p.r};
    
                        pCell.c += c;
                        pCell.r += r;
                        
                        const x = GridContainer.CELL_SIZE * (pCell.c - (Templok.Engine.GRID_SIZE / 2 - .5));
                        const y = GridContainer.CELL_SIZE * (pCell.r - (Templok.Engine.GRID_SIZE / 2 - .5));
        
                        const marker = new Phaser.GameObjects.Image(this.scene, x, y, "texture_atlas_3", "highlighted_cell");
                        this.markersContainer.add(marker);

                        this.markers.push(marker);
                    }
                }
            }
        }

        this.markersContainer.alpha = 0;

        this.scene.tweens.add({
            targets: this.markersContainer,
            alpha: 1,
            ease: Phaser.Math.Easing.Cubic.Out,
            duration: 450
        });
    }

    public unMarkDropPosition(): void {

        this.scene.tweens.add({
            targets: this.markersContainer,
            alpha: 0,
            ease: Phaser.Math.Easing.Cubic.Out,
            duration: 450,
            onComplete: function(): void {

                for (let i = 0; i < this.markers.length; i ++) {
                    this.markers[i].destroy();
                }
        
                this.markers = [];
            },
            onCompleteScope: this
        });
    }

    public markShadows(p: {c: number, r: number}, blockData: Templok.BlockData): void {

        const data = blockData.data[blockData.i];
        
        if (data === 0x0001 || data === 0x0002) {

            for (let i = 0; i < 5; i ++) {

                const pCell = {c: p.c, r: p.r};

                if (data === 0x0001) {
                    pCell.c += i;
                } else {
                    pCell.r += i;
                }

                const x = GridContainer.CELL_SIZE * (pCell.c - (Templok.Engine.GRID_SIZE / 2 - .5));
                const y = GridContainer.CELL_SIZE * (pCell.r - (Templok.Engine.GRID_SIZE / 2 - .5));

                const shadow = this.shadows[i];
                shadow.x = x;
                shadow.y = y;
                shadow.visible = true;
            }

        } else {

            let i = 0;

            for (let c = 0; c < 4; c++) {
                for (let r = 0; r < 4; r++) {
                    if (data & (0x8000 >> (c * 4 + r))) {

                        const pCell = {c: p.c, r: p.r};
    
                        pCell.c += c;
                        pCell.r += r;
                        
                        const x = GridContainer.CELL_SIZE * (pCell.c - (Templok.Engine.GRID_SIZE / 2 - .5));
                        const y = GridContainer.CELL_SIZE * (pCell.r - (Templok.Engine.GRID_SIZE / 2 - .5));
        
                        const shadow = this.shadows[i];
                        shadow.x = x;
                        shadow.y = y;
                        shadow.visible = true;

                        i ++;
                    }
                }
            }
        }
    }

    public unMarkShadows(): void {
        
        for (let i = 0; i < this.shadows.length; i ++) {
            this.shadows[i].visible = false;
        }

        if (this.specialCellHoverEffect) {
            this.specialCellHoverEffect.disappear();
            this.specialCellHoverEffect = null;
        }

        this.premarkedFeatureEffectPlayed = false;
    }

    public markPossibleLinesAndColumns(lines: number[], columns: number[], duplicatedCells: {p: {c: number, r: number}, type: number}[]): void {

        this.possibleLines = lines;
        this.possibleColumns = columns;

        if (this.possibleLines.length > 0 || this.possibleColumns.length > 0) {
            this.markingPossibleLinesAndColumns = true;
        } else {
            this.markingPossibleLinesAndColumns = false;
        }
       
        for (let i = 0; i < this.possibleLines.length; i++) {

            const r = this.possibleLines[i];

            for (let c = 0; c < Templok.Engine.GRID_SIZE; c ++) {

                const cell = this.getCell({c: c, r: r});

                if (cell) {
                    cell.markHover();
                }
            }
        }

        for (let i = 0; i < this.possibleColumns.length; i++) {

            const c = this.possibleColumns[i];

            for (let r = 0; r < Templok.Engine.GRID_SIZE; r ++) {

                const cell = this.getCell({c: c, r: r});

                if (cell) {
                    cell.markHover();
                }
            }
        }

        if (duplicatedCells.length > 0) {

            if (this.specialCellHoverEffect === null) {

                this.specialCellHoverEffect = new SpecialCellHoverEffect(this.scene, duplicatedCells);
                this.add(this.specialCellHoverEffect);
            }

            if (!this.premarkedFeatureEffectPlayed) {
                this.premarkedFeatureEffectPlayed = true;
                AudioManager.playSound("premarked_feature");
            }

        } else {

            this.premarkedFeatureEffectPlayed = false;
        }
    }

    public unMarkPossibleLinesAndColumns(): void {

        this.markingPossibleLinesAndColumns = false; 
        
        for (let i = 0; i < this.cells.length; i++) {
            this.cells[i].unMarkHover();
        }
    }

    public onGameOver(): void {

        for (let i = 0; i < this.cells.length;  i++) {

            this.cells[i].onGameOver();

            const n = Math.floor(Math.random() * 8);

            for (let k = 0; k < n; k ++) {

                const delay = Math.floor(550 * Math.random());

                this.scene.time.delayedCall(delay, function(): void {
                    this.bubblesContainer.emitBubbles(this.cells[i]);
                }, [], this);
            }
        }
    }

    private getCell(p: {c: number, r: number}): Cell {

        let cell: Cell = null;

        for (let i = 0; i < this.cells.length; i ++) {

            if (this.cells[i].p.c === p.c && this.cells[i].p.r === p.r) {
                cell = this.cells[i];
                break;
            }
        }

        return cell;
    }

    private checkCellsForInconsistencies(): void {
 
        const board = JSON.parse(JSON.stringify(GameVars.templokEngine.board));
        const inconsistentCells: Cell [] = [];

        for (let i = 0; i < this.cells.length; i ++) {

            const cell = this.cells[i];
            const p = cell.p;

            if (!cell.disappearing && board[p.c][p.r] === 0) {
                inconsistentCells.push(cell);
            }
        }

        for (let i = 0; i < inconsistentCells.length; i ++) {

            this.removeCell(inconsistentCells[i]);
        }
    }
}
